import { Link } from "react-router-dom";
import { Instagram, Mail, ArrowUpRight } from "lucide-react";
import GoogleIcon from "@/components/icons/GoogleIcon";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative pt-24 pb-10 bg-ink-900 text-cream-50 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gold-line opacity-70" />
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.15), transparent 60%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-8 items-start">
          {/* Logo + tagline */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-4" aria-label="Captured Moments — Home">
              <img
                src="/assets/logo/logo-white.png"
                alt="Captured Moments Photo Booth"
                className="h-28 md:h-36 w-auto"
              />
            </Link>
            <p className="mt-6 font-display text-2xl md:text-3xl text-cream-50 leading-tight max-w-md">
              Elevating your event one <span className="italic gold-text-gradient">captured moment</span> at a time.
            </p>
            <p className="mt-4 text-cream-50/70 text-sm max-w-sm">
              Luxury photo booth experiences across Southern California.
            </p>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <div className="overline mb-5 text-gold-300">Explore</div>
            <ul className="space-y-3">
              {[
                ["Home", "/#hero"],
                ["About", "/#about"],
                ["Booths", "/#booths"],
                ["Backdrops", "/#backdrops"],
                ["Process", "/#how-it-works"],
                ["Events", "/#services"],
                ["Gallery", "/#gallery"],
                ["Reviews", "/#reviews"],
                ["FAQ", "/#faq"],
                ["Contact", "/contact"],
              ].map(([label, href]) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-cream-50/80 hover:text-gold-300 transition-colors text-sm tracking-wide"
                    data-testid={`footer-link-${label.toLowerCase()}`}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="overline mb-5 text-gold-300">Connect</div>
            <a
              href="mailto:cmphotoboothco@gmail.com"
              className="group inline-flex items-center gap-3 text-cream-50 hover:text-gold-300 transition-colors"
              data-testid="footer-email"
            >
              <Mail size={16} className="text-gold-400" />
              <span className="text-sm tracking-wide">cmphotoboothco@gmail.com</span>
            </a>
            <div className="mt-3">
              <a
                href="https://www.instagram.com/CapturedMoments.PB"
                target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 text-cream-50 hover:text-gold-300 transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={16} className="text-gold-400" />
                <span className="text-sm tracking-wide">@CapturedMoments.PB</span>
                <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </a>
            </div>

            <div className="mt-3">
              <a
                href="https://share.google/4rdA2JFo6zTSBbgBa"
                target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 text-cream-50 hover:text-gold-300 transition-colors"
                data-testid="footer-google"
              >
                <GoogleIcon size={16} />
                <span className="text-sm tracking-wide">Google Business Profile</span>
                <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </a>
            </div>

            <Link
              to="/contact"
              className="mt-8 btn-luxe inline-flex items-center px-7 py-4 text-[11px]"
              data-testid="footer-contact-cta"
            >
              Begin Your Inquiry
            </Link>
          </div>
        </div>

        <div className="luxe-divider my-10 opacity-60" />

        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-cream-50/55 tracking-wide">
          <div>© {year} Captured Moments. All Rights Reserved.</div>
          <div className="uppercase tracking-[0.22em]">Crafted for unforgettable evenings</div>
        </div>
      </div>
    </footer>
  );
}
